/* eslint-disable @typescript-eslint/no-explicit-any */
import { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';

import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/base/AdvanceTable';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import bonusItemService from 'service/bonusItem';

export type BonusResultData = {
  product_name: string;
  variation: string;
  barcode: string;
  area: string;
  bonus_activation: string;
  bonus_payment: string;
  quantity: string;
  quantity_good: string;
  sum: string;
  currency: string;
  used: string;
  remainder: string;
  status: string;
  guid: string;
};

interface AddedProductsTableProps {
  selectedBonusId: string;
}
const AddedProductsTable = ({ selectedBonusId }: AddedProductsTableProps) => {
  const { t, i18n } = useTranslation();

  const { data } = useQuery(
    ['GET_BONUS_ITEM', selectedBonusId],
    () => {
      if (selectedBonusId !== '') {
        const res = bonusItemService
          .getList({
            offset: 0,
            limit: 10,
            bonus_id: selectedBonusId
          })
          .then((res: any) => {
            return res?.items;
          });
        return res;
      }
    },
    { enabled: !!selectedBonusId }
  );

  const bonusresultData: BonusResultData[] = useMemo(() => {
    const resultData =
      data?.map((items: any) => {
        return {
          guid: items?.id,
          product_name: items?.product?.name,
          barcode: items?.barcode,
          area: items?.bonus_application?.name?.[i18n?.language],
          bonus_activation: items?.bonus_activation?.name?.[i18n?.language],
          bonus_payment: items?.bonus_payment?.name?.[i18n?.language],
          currency: items?.currency?.name?.[i18n?.language],
          quantity: items?.quantity ?? 0,
          quantity_good: items?.buy_quantity ?? 0,
          sum: items?.amount
            ? new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 4,
                useGrouping: true
              })
                .format(items?.amount)
                .replace(/,/g, ' ')
            : 0,
          used: items?.quantity_used ?? 0,
          remainder:
            items?.quantity && items?.quantity_used
              ? items?.quantity - items?.quantity_used
              : 0,
          status: items?.status === true ? t('active') : t('blocked')
        };
      }) ?? [];

    return resultData;
  }, [data, selectedBonusId, t, i18n?.language]);

  const bonusDataTableColumns: ColumnDef<BonusResultData>[] = [
    {
      accessorKey: 'product_name',
      header: t('bonus_names'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'barcode',
      header: t('barcode'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'area',
      header: t('scope_application'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'bonus_activation',
      header: t('bonus_activation'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'bonus_payment',
      header: t('bonus_payment'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'quantity',
      header: t('quantity'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'quantity_good',
      header: t('quantity_goods_pruchased'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'sum',
      header: t('amounts'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'currency',
      header: t('currency'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'used',
      header: t('used'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'remainder',
      header: t('remainder'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'status',
      header: t('status'),
      cell: rowData => {
        const { status } = rowData?.row?.original || {};
        /* prettier-ignore */
        const defaultColors: any = {
             [t('active')]: { bg: '#BBFBD0', text: '#119C2B' },
             [t('blocked')]: { bg: '#FDDFDD', text: '#F2271C' }
           };
        /* prettier-ignore */
        const backgroundColor =
            defaultColors[status]?.bg || '#ffffff'; // Default to white if not specified
        const textColor = defaultColors[status]?.text || '#000000';

        return (
          <div
            style={{
              background: backgroundColor,
              padding: '8px 10px',
              textAlign: 'center',
              borderRadius: '8px',
              color: textColor,
              fontWeight: '600'
            }}
          >
            {status}
          </div>
        );
      },
      meta: {
        cellProps: { className: 'text-900' }
      }
    }
  ];

  const table = useAdvanceTable({
    data: bonusresultData,
    columns: bonusDataTableColumns,
    pageSize: 100,
    pagination: true,
    sortable: true,
    selection: false
  });

  return (
    <>
      <AdvanceTableProvider {...table}>
        <AdvanceTable tableProps={{ className: 'phoenix-table fs-9' }} />
        <AdvanceTableFooter pagination />
      </AdvanceTableProvider>
    </>
  );
};

export default AddedProductsTable;
